import { defineStore } from "pinia";
import { watch as watchLive } from "@/components/toLoad/live";
import { apiUser } from "@/components/toLoad/api";
import { setSnackbar } from "@/stores/ui";

function createdMs(value) {
  if (!value) return 0;
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (value._seconds != null) return value._seconds * 1000;
  if (value.seconds != null) return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function initials(name) {
  if (!name) return "?";
  return String(name)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");
}

const CHANNELS = new Set(["company", "account", "team"]);

function storeKey(inbox, id) {
  return `${inbox}:${id}`;
}

function announceNewNotifications(previousKeys, nextById, nextIds) {
  const fresh = nextIds
    .filter((key) => !previousKeys.has(key))
    .map((key) => nextById[key])
    .filter((item) => item && !item.read);
  if (!fresh.length) return;
  if (fresh.length === 1) {
    setSnackbar({
      text: `Vous avez reçu une notification : ${fresh[0].title}`,
      color: "info",
      timeout: 6000,
    });
    return;
  }
  setSnackbar({
    text: `${fresh.length} nouvelles notifications`,
    color: "info",
    timeout: 6000,
  });
}

export const useNotificationsStore = defineStore("notifications", {
  state: () => ({
    byId: {},
    ids: [],
    companyId: null,
    loadStatus: "idle",
    unsubCompany: null,
    unsubAccount: null,
    companyDocs: [],
    accountDocs: [],
    channel: "company",
    readVisibleLimit: 10,
  }),

  getters: {
    list(state) {
      return state.ids
        .map((id) => state.byId[id])
        .filter(Boolean)
        .sort((a, b) => createdMs(b.createdAt) - createdMs(a.createdAt));
    },

    unreadCount(state) {
      return state.ids
        .map((id) => state.byId[id])
        .filter((item) => item && !item.read)
        .length;
    },

    unreadByChannel(state) {
      const counts = { company: 0, account: 0, team: 0 };
      for (const id of state.ids) {
        const item = state.byId[id];
        if (!item || item.read) continue;
        if (CHANNELS.has(item.channel)) counts[item.channel] += 1;
      }
      return counts;
    },

    channelItems(state) {
      return state.ids
        .map((id) => state.byId[id])
        .filter(Boolean)
        .filter((item) => item.channel === state.channel)
        .sort((a, b) => createdMs(b.createdAt) - createdMs(a.createdAt));
    },

    filtered() {
      let readKept = 0;
      return this.channelItems.filter((item) => {
        if (!item.read) return true;
        if (readKept >= this.readVisibleLimit) return false;
        readKept += 1;
        return true;
      });
    },

    hasMoreRead() {
      const readCount = this.channelItems.filter((item) => item.read).length;
      return readCount > this.readVisibleLimit;
    },

    grouped() {
      const groups = { today: [], earlier: [] };
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      const startMs = start.getTime();

      for (const item of this.filtered)
        if (createdMs(item.createdAt) >= startMs) groups.today.push(item);
        else groups.earlier.push(item);
      return groups;
    },
  },

  actions: {
    resetState() {
      if (this.unsubCompany) {
        this.unsubCompany();
        this.unsubCompany = null;
      }
      if (this.unsubAccount) {
        this.unsubAccount();
        this.unsubAccount = null;
      }
      this.byId = {};
      this.ids = [];
      this.companyDocs = [];
      this.accountDocs = [];
      this.companyId = null;
      this.loadStatus = "idle";
      this.readVisibleLimit = 10;
    },

    mergeDocs() {
      const wasReady = this.loadStatus === "ready";
      const previousKeys = new Set(this.ids);
      const nextById = {};
      const nextIds = [];
      for (const raw of [...this.companyDocs, ...this.accountDocs]) {
        const key = storeKey(raw.inbox || "company", raw.id);
        nextById[key] = {
          ...raw,
          storeKey: key,
          actorInitials: initials(raw.actor?.displayName || raw.title),
        };
        nextIds.push(key);
      }
      this.byId = nextById;
      this.ids = nextIds;
      this.loadStatus = "ready";
      if (wasReady) announceNewNotifications(previousKeys, nextById, nextIds);
    },

    ensureWatch(companyId) {
      if (!companyId) {
        this.resetState();
        return;
      }

      if (this.companyId === companyId && this.unsubCompany && this.unsubAccount) return;

      if (this.unsubCompany) {
        this.unsubCompany();
        this.unsubCompany = null;
      }
      if (this.unsubAccount) {
        this.unsubAccount();
        this.unsubAccount = null;
      }

      if (this.companyId !== companyId) {
        this.byId = {};
        this.ids = [];
        this.companyDocs = [];
        this.accountDocs = [];
        this.readVisibleLimit = 10;
      }

      this.companyId = companyId;
      this.loadStatus = this.ids.length ? "refreshing" : "loading";

      const scopedCompanyId = companyId;
      this.unsubCompany = watchLive(scopedCompanyId, "myNotifications", (res) => {
        if (this.companyId !== scopedCompanyId) return;
        this.companyDocs = (res.docs || []).map((document) => ({
          ...document.data(),
          id: document.id,
          inbox: "company",
        }));
        this.mergeDocs();
      });

      this.unsubAccount = watchLive(scopedCompanyId, "myAccountNotifications", (res) => {
        if (this.companyId !== scopedCompanyId) return;
        this.accountDocs = (res.docs || []).map((document) => ({
          ...document.data(),
          id: document.id,
          inbox: "account",
        }));
        this.mergeDocs();
      });
    },

    setChannel(channel) {
      if (!CHANNELS.has(channel) || this.channel === channel) return;
      this.channel = channel;
      this.readVisibleLimit = 10;
    },

    loadMoreRead() {
      this.readVisibleLimit += 10;
    },

    async markRead(storeKeyOrId) {
      const current = this.byId[storeKeyOrId];
      if (!current) return;
      this.byId[storeKeyOrId] = { ...current, read: true };
      if (current.inbox === "account") {
        await apiUser({
          method: "patch",
          url: `/me/notifications/${current.id}`,
          data: { read: true },
        });
        return;
      }
      if (!this.companyId) return;
      await apiUser({
        method: "patch",
        url: `/companies/${this.companyId}/me/notifications/${current.id}`,
        data: { read: true },
      });
    },

    async markAllRead() {
      const next = { ...this.byId };
      for (const id of this.ids)
        if (next[id]) next[id] = { ...next[id], read: true };
      this.byId = next;
      const jobs = [
        apiUser({
          method: "post",
          url: "/me/notifications/read-all",
        }),
      ];
      if (this.companyId)
        jobs.push(apiUser({
          method: "post",
          url: `/companies/${this.companyId}/me/notifications/read-all`,
        }));
      await Promise.all(jobs);
    },

    async remove(storeKeyOrId) {
      const current = this.byId[storeKeyOrId];
      if (!current) return;
      if (current.inbox === "account") {
        this.accountDocs = this.accountDocs.filter((doc) => doc.id !== current.id);
        await apiUser({
          method: "delete",
          url: `/me/notifications/${current.id}`,
        });
      } else {
        if (!this.companyId) return;
        this.companyDocs = this.companyDocs.filter((doc) => doc.id !== current.id);
        await apiUser({
          method: "delete",
          url: `/companies/${this.companyId}/me/notifications/${current.id}`,
        });
      }
      this.mergeDocs();
    },
  },
});
